// Base types
export interface BaseEntity {
  id: number;
  created_at: string;
  updated_at: string;
}

// User types
export interface User extends BaseEntity {
  email: string;
  username: string;
  is_active: boolean;
  is_superuser: boolean;
}

export interface UserCreate {
  email: string;
  username: string;
  password: string;
}

export interface UserLogin {
  username: string;
  password: string;
}

export interface Token {
  access_token: string;
  token_type: string;
  expires_in: number;
}

// Document types
export enum DocumentStatus {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  FAILED = 'failed',
}

export interface Document extends BaseEntity {
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  status: DocumentStatus;
  folder_id?: string;
  page_count?: number;
  title?: string;
  author?: string;
  subject?: string;
  creator?: string;
  producer?: string;
  creation_date?: string;
  modification_date?: string;
  processing_started_at?: string;
  processing_completed_at?: string;
  processing_error?: string;
  processing_progress?: number;
  processing_stage?: string;
  owner_id: number;
  access_scope?: 'owned' | 'organization' | 'vault' | 'restricted';
  can_delete?: boolean;
  is_vault_document?: boolean;
  vault_doc_id?: string;
  project_id?: string;
  doc_type?: string;
  source?: string;
  ocr_quality_score?: number;  // 0-100
}

export interface DocumentUpload {
  document_id: number;
  filename: string;
  status: DocumentStatus;
  message: string;
}

export interface DocumentProcessingStatus {
  document_id: number;
  status: DocumentStatus;
  progress?: number;
  message?: string;
  error?: string;
  started_at?: string;
  completed_at?: string;
  stage?: string;
  ocr_quality_score?: number;  // 0-100
  ocr_engine?: string;
  page_count?: number;
}

export interface DocumentPreview {
  document_id: number;
  filename: string;
  status: string;
  ocr_engine: string;
  ocr_quality_score: number | null;
  ocr_languages: string | null;
  low_quality_pages: number[];
  page_count: number;
  char_count: number;
  avg_chars_per_page: number;
  looks_unreliable: boolean;
  processing_stage: string | null;
  processing_error: string | null;
  preview: string;
  preview_truncated: boolean;
}

// Valuation and underwriting tabs
export interface ValuationFinding {
  rule_id: string;
  severity: 'info' | 'warning' | 'critical';
  status: 'PASS' | 'WARNING' | 'CRITICAL' | 'NOT_APPLICABLE';
  message_he: string;
  evidence?: Record<string, any>;
  recommended_action?: string | null;
}

export interface PropertyValuation {
  id: string;
  project_id: string;
  document_id: number;
  status: string;
  appraisal_date?: string | null;
  valid_until?: string | null;
  market_value_ils?: number | null;
  forced_sale_value_ils?: number | null;
  requested_credit_ils?: number | null;
  ltv_ratio_bps?: number | null;
  verdict?: 'APPROVE' | 'APPROVE_WITH_CONDITIONS' | 'HOLD' | 'REJECT' | null;
  verdict_confidence_bps?: number | null;
  findings: ValuationFinding[];
  comparables: Array<Record<string, any>>;
  created_at?: string | null;
}

export type ValuationSummary = PropertyValuation;

export interface ValuationAgentResponse {
  answer: string;
  artifacts: Artifact[];
  sources?: Record<string, any>;
}

export interface UnderwritingContext {
  id: string;
  project_id: string;
  status: string;
  context_version: string;
  selected_document_ids: number[];
  valuation_id?: string | null;
  borrower_summary?: Record<string, any>;
  collateral_summary?: Record<string, any>;
  debt_summary?: Record<string, any>;
  risk_summary?: {
    missing_inputs?: string[];
    risk_flags?: string[];
    recommended_actions?: string[];
  };
  verdict?: {
    verdict: string;
    confidence_bps?: number | null;
    recommended_actions?: string[];
  } | null;
  created_at?: string | null;
}

export type UnderwritingContextSnapshot = UnderwritingContext;

// Analysis types
export enum AnalysisStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum AnalysisType {
  LOCAL = 'local',
  CLOUD = 'cloud',
  HYBRID = 'hybrid',
}

export interface Analysis extends BaseEntity {
  document_id: number;
  user_id: number;
  analysis_type: AnalysisType;
  status: AnalysisStatus;
  model_name?: string;
  model_version?: string;
  summary?: string;
  key_metrics?: Record<string, any>;
  insights?: Insight[];
  recommendations?: Recommendation[];
  risk_assessment?: RiskAssessment;
  processing_time_seconds?: number;
  tokens_used?: number;
  cost_usd?: number;
  error_message?: string;
  retry_count: number;
  started_at?: string;
  completed_at?: string;
}

export interface AnalysisRequest {
  document_id: number;
  analysis_type: AnalysisType;
  options?: Record<string, any>;
}

export interface AnalysisStatusResponse {
  analysis_id: number;
  status: AnalysisStatus;
  progress?: number;
  message?: string;
  error?: string;
  started_at?: string;
  completed_at?: string;
}

export interface FinancialMetrics {
  revenue?: number;
  net_income?: number;
  total_assets?: number;
  total_liabilities?: number;
  equity?: number;
  cash_flow?: number;
  debt_to_equity_ratio?: number;
  return_on_equity?: number;
  return_on_assets?: number;
  current_ratio?: number;
}

export interface Insight {
  title: string;
  description: string;
  category: string;
  confidence: number;
  supporting_data?: Record<string, any>;
}

export interface Recommendation {
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  implementation_notes?: string;
}

export interface RiskAssessment {
  overall_risk_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  risk_factors: RiskFactor[];
  mitigation_strategies: MitigationStrategy[];
  monitoring_recommendations: string[];
}

export interface RiskFactor {
  factor: string;
  impact: 'low' | 'medium' | 'high';
  probability: 'low' | 'medium' | 'high';
}

export interface MitigationStrategy {
  strategy: string;
  priority: 'low' | 'medium' | 'high';
}

// Report types
export enum ReportStatus {
  PENDING = 'pending',
  GENERATING = 'generating',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum ReportFormat {
  HTML = 'html',
  PDF = 'pdf',
  DOCX = 'docx',
}

export interface Report extends BaseEntity {
  analysis_id: number;
  title: string;
  format: ReportFormat;
  status: ReportStatus;
  html_content?: string;
  file_path?: string;
  word_count?: number;
  page_count?: number;
  chart_count?: number;
  generation_time_seconds?: number;
  error_message?: string;
  generated_at?: string;
}

// API Response types
export interface PaginationParams {
  page: number;
  size: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ErrorResponse {
  error: string;
  detail?: string;
  code?: string;
}

export interface SuccessResponse {
  message: string;
  data?: Record<string, any>;
}

// UI Component types
export interface LoadingState {
  isLoading: boolean;
  error?: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface FileUpload {
  file: File;
  progress: UploadProgress;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  document?: Document;
}

// W3 Auth types
export interface W3AuthCredentials {
  username: string;
  password: string;
}

/**
 * W3 login response. Either success fields are populated (tokens), OR challenge
 * fields are populated (when MFA / first-login password change is required).
 * The frontend should branch on `challenge` first.
 */
export interface W3Token {
  // Success fields (present on full authentication)
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  org_id?: string;
  user_id?: string;
  mcp_connected?: boolean;
  mcp_tool_count?: number;
  // Challenge fields (present when more steps are needed)
  challenge?:
    | 'MFA_SETUP'
    | 'SOFTWARE_TOKEN_MFA'
    | 'EMAIL_OTP'
    | 'SMS_MFA'
    | 'SELECT_MFA_TYPE'
    | 'NEW_PASSWORD_REQUIRED';
  session?: string;
  message?: string;
  // Masked code destination for EMAIL_OTP / SMS_MFA (e.g. "m***@h***.co.il").
  destination?: string;
  // Methods to choose from on the SELECT_MFA_TYPE challenge.
  available_mfa_types?: string[];
}

export interface W3MfaAssociateResponse {
  secret_code: string;
  session: string;
  otpauth_url: string;
}

export interface W3MfaVerifyResponse {
  status?: string;
  session?: string;
}

export interface McpTool {
  id: string;
  name: string;
  description: string;
  category: string;
}

export interface McpStatus {
  connected: boolean;
  has_token: boolean;
  tool_count: number;
  tools: McpTool[];
}

// Folder/File Tree types
export interface FileNode {
  id: string;
  name: string;
  type: 'folder' | 'file';
  children?: FileNode[];
  parentId?: string;
  isPrivateVault?: boolean;
  isRecycleBin?: boolean;
  isOrgFolder?: boolean;
  isProjectFolder?: boolean;
  document?: Document;
  fileCount?: number;
}

export interface FolderCreate {
  name: string;
  parent_id?: string;
}

// Chat types
export interface ChatMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  artifacts?: Artifact[];
  fileContext?: string[];
  attachedFiles?: string[];
  created_at: string;
  isStreaming?: boolean;
  kind?: 'compaction';
}

export interface ChatSession {
  id: string;
  title?: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  domain?: 'appraisal' | 'legal' | 'financial';
}

export interface ChatJobAccepted {
  job_id: string;
  session_id: string;
  status: string;
}

export interface ChatJobStatus {
  status: 'pending' | 'done' | 'error';
  message?: ChatMessage | null;
  error_code?: string | null;
  notice?: ChatMessage | null;
}

// Opinion-skill async jobs (senior-opinion / internal-review) — submit + poll.
export interface RecipeJobAccepted {
  job_id: string;
  recipe_id: string;
  status: string;
}

export interface RecipeJobStatus {
  status: 'pending' | 'done' | 'error';
  recipe_id?: string | null;
  content?: string | null;       // opinion_markdown
  structured?: any | null;
  model_used?: string | null;
  id?: string | null;
  error_code?: string | null;
}

export interface LegalSkill {
  id: string;
  name_he: string;
  description: string;
}

export interface TaseFiling {
  title: string;
  period: string;
  year: number;
  pages?: number;
  url: string;
}

export interface TaseCompany {
  name: string;
  sector?: string;
  filings: TaseFiling[];
}

export interface TaseCompanySearchResult {
  company_id: number;
  name: string;
  label: string;
  company_type: 'COMPANY' | 'NONTRADABLE' | string;
  suggested_profile: AnalysisProfile;
}

export interface TaseLiveFiling extends TaseFiling {
  report_id: number;
  published_at: string;
  company_id: number;
  company_name: string;
  tase_issuer_id?: string | null;
  report_page_url: string;
  html_url?: string | null;
  xbrl_url?: string | null;
  ixbrl_url?: string | null;
  ixbrl_report_id?: number | null;
  structured_available: boolean;
  structured_format?: 'ixbrl' | null;
}

// Public-company reports corpus dashboard
export type AnalysisProfile = 'general' | 'real_estate';
export type CorpusRunStatus = 'pending' | 'running' | 'done' | 'error';
export type CorpusValueStatus = 'filled' | 'calculated' | 'missing' | 'not_applicable' | 'needs_review' | 'flagged';

export interface CorpusRunStats {
  items_total?: number;
  total?: number;
  filled?: number;
  calculated?: number;
  missing?: number;
  not_applicable?: number;
  needs_review?: number;
  flagged?: number;
  grounded?: number;
  w3_calls?: number;
  categories?: Record<string, Record<string, number>>;
  [key: string]: unknown;
}

export interface CorpusRunSummary {
  run_id: string;
  status: CorpusRunStatus;
  difficulty?: 'easy' | 'hard' | 'mixed' | null;
  spec_version_id: string;
  spec_version_label: string;
  spec_profile: AnalysisProfile;
  stats?: CorpusRunStats | null;
  error?: string | null;
  created_at?: string | null;
  finished_at?: string | null;
}

export interface CompanyReportSummary {
  id: string;
  company_id: string;
  company_name: string;
  analysis_profile: AnalysisProfile;
  document_id: number;
  document_name: string;
  document_status: string;
  processing_progress?: number | null;
  processing_stage?: string | null;
  page_count?: number | null;
  period_year: number;
  period_quarter?: number | null;
  report_type: 'annual' | 'quarterly';
  created_at?: string | null;
  latest_run?: CorpusRunSummary | null;
}

export interface CompanyReportCreated {
  id: string;
  company_id: string;
  company_name: string;
  analysis_profile: AnalysisProfile;
  document_id: number;
  period_year: number;
  period_quarter?: number | null;
  report_type: 'annual' | 'quarterly';
}

export interface CompanySummary {
  id: string;
  name: string;
  tase_issuer_id?: string | null;
  sector?: string | null;
  analysis_profile: AnalysisProfile;
  report_count: number;
  latest_report?: CompanyReportSummary | null;
}

export interface CompanyDetail {
  id: string;
  name: string;
  tase_issuer_id?: string | null;
  sector?: string | null;
  analysis_profile: AnalysisProfile;
  reports: CompanyReportSummary[];
}

export interface CompanyReportDetail {
  report: CompanyReportSummary;
  runs: CorpusRunSummary[];
}

export interface CorpusValue {
  item_key: string;
  category: string;
  name?: string | null;
  source_hint?: string | null;
  calc?: string | null;
  notes?: string | null;
  difficulty?: 'easy' | 'hard' | 'mixed' | null;
  value?: string | null;
  unit?: string | null;
  quote?: string | null;
  section_title?: string | null;
  page?: number | null;
  grounded?: boolean | null;
  status: CorpusValueStatus;
  extra?: {
    derived?: boolean;
    formula?: string;
    reason?: string;
    inputs?: Array<{ item_key: string; value?: string | null; unit?: string | null }>;
    [key: string]: unknown;
  } | null;
}

export type CompletionReadinessLevel = 'extracting' | 'completing' | 'ready_with_limitations' | 'decision_ready';
export type CompletionEvidenceStatus =
  | 'verified'
  | 'calculated'
  | 'candidate'
  | 'not_found'
  | 'not_disclosed'
  | 'not_applicable'
  | 'external_required'
  | 'extraction_failed'
  | 'ocr_failed'
  | 'contradictory'
  | 'superseded';
export type CompletionResolutionStatus =
  | 'open'
  | 'auto_retry'
  | 'manual_review'
  | 'waiting_external'
  | 'resolved'
  | 'accepted_limitation';
export type CompletionFinancialStatus = 'not_assessed' | 'positive' | 'neutral' | 'warning' | 'critical';

export interface CompletionCaseStats {
  items_total: number;
  applicable_total: number;
  not_applicable: number;
  evidence_covered: number;
  evidence_coverage_pct: number;
  coverage_target_pct: number;
  critical_total: number;
  critical_covered: number;
  required_total: number;
  required_covered: number;
  manual_review: number;
  open_gaps: number;
  contradictions: number;
  accepted_limitations: number;
  provenance_complete: number;
  provenance_integrity_pct: number;
  decision_ready: boolean;
  covenants_evaluated?: number;
  covenant_breaches?: number;
  covenant_near_limits?: number;
  [key: string]: unknown;
}

export interface CompletionCase {
  case_id: string;
  baseline_run_id: string;
  analysis_profile: AnalysisProfile;
  status: 'open' | 'finalized';
  readiness_level: CompletionReadinessLevel;
  rules_version: string;
  stats?: CompletionCaseStats | null;
  created_at?: string | null;
  updated_at?: string | null;
  finalized_at?: string | null;
}

export interface CompletionEvidence {
  evidence_id: string;
  source_type: string;
  source_document_id?: number | null;
  source_url?: string | null;
  value?: string | null;
  unit?: string | null;
  quote?: string | null;
  section_title?: string | null;
  page?: number | null;
  period_year?: number | null;
  period_quarter?: number | null;
  scope?: 'consolidated' | 'solo' | 'contractual' | 'unknown' | null;
  method: string;
  grounded?: boolean | null;
  approvable?: boolean;
  created_at?: string | null;
}

export interface CompletionItem {
  item_key: string;
  category: string;
  name?: string | null;
  source_hint?: string | null;
  required_level: 'critical' | 'required' | 'optional';
  applicability: 'applicable' | 'unknown' | 'not_applicable';
  applicability_reason?: string | null;
  evidence_status: CompletionEvidenceStatus;
  resolution_status: CompletionResolutionStatus;
  financial_status: CompletionFinancialStatus;
  limitation_text?: string | null;
  selected_evidence_id?: string | null;
  evidence: CompletionEvidence[];
}

export interface CompletionTargetedJob {
  job_id: string;
  case_id: string;
  item_key?: string | null;
  job_type: string;
  status: 'pending' | 'running' | 'done' | 'error';
  result?: {
    status?: string;
    reason?: string | null;
    w3_calls?: number;
    est_tokens?: number;
  } | null;
  error?: string | null;
}

export type ArtifactType = 'image' | 'chart' | 'html' | 'table' | 'code' | 'markdown';

export interface Artifact {
  id: string;
  type: ArtifactType;
  data: any;
  title?: string;
  mime_type?: string;
}

// Tool types
export interface Tool {
  id: string;
  name: string;
  description: string;
  icon?: string;
  enabled?: boolean;
}

// Form types
export interface LoginForm {
  username: string;
  password: string;
}

export interface AnalysisForm {
  document_id: number;
  analysis_type: AnalysisType;
  options?: Record<string, any>;
}

// Product usage is a billing/audit dimension, independent of agent expertise.
export type ProductUsageCategory = 'chat_agent' | 'public_reports';

export interface QuotaCategoryUsage {
  category: string;
  tokens: number;
}

/** Old response shape accepted only during a rolling deployment. */
export interface LegacyQuotaDomainUsage {
  domain: string;
  tokens: number;
}

export interface QuotaStatus {
  available: boolean;
  limit: number | null;
  used: number | null;
  remaining: number | null;
  reset_at: string | null;
  estimated: boolean;
  year_month: string;
  tracked_used: number;
  usage_scope: 'organization' | 'user';
  by_category?: QuotaCategoryUsage[];
  by_domain?: LegacyQuotaDomainUsage[];
}
